import {
  Box,
  Flex,
  Menu,
  Portal,
  Text,
  Image,
  HStack,
  Separator,
  Center,
  Circle,
  Span,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import { useState, useMemo, useCallback } from 'react';
import {
  Avatar,
  type AvatarProps,
  GenderAge,
  LogoutIcon,
  ProfileIcon,
  QrCodeIcon,
  SettingIcon,
  tools,
  ConfirmDialog,
  CopyIcon,
  EditIconGhost,
  LogReportIcon,
} from '@halo/ui-chat-react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useSessionStore } from '@/store/session';
import { useActionsStore } from '@/store/actions';
import Paths from '@/configs/router/paths';
import MyCenterBg from '@/assets/images/common/images/myCenter.jpg';
import { useAppStore, WsStatus } from '@/store/client';
import { EditFormDialog } from '@/components/ProfileEditDialog/EditFormDialog';
import { EditItem } from '@/components/ProfileEditDialog/helpers';
import { useCountries } from '@/scripts/hooks/useCountries';

const MyAvatar = memo(function MyAvatar(props: AvatarProps) {
  const { wsStatus, user } = useAppStore();

  const statusIcon = useMemo(() => {
    switch (wsStatus) {
      case WsStatus.Open:
        return <Circle bg="green.500" size="0.75em" outline="0.25em solid" outlineColor="bg" />;
      case WsStatus.Connecting:
      case WsStatus.Closed:
        return <Circle bg="orange.100" size="0.75em" outline="0.25em solid" outlineColor="bg" />;
      case WsStatus.Error:
        return <Circle bg="fg.error" size="0.75em" outline="0.25em solid" outlineColor="bg" />;
    }
  }, [wsStatus]);

  return (
    <Avatar src={user?.avatar} name={user?.name} cursor="pointer" icon={statusIcon} {...props} />
  );
});

const menuPositioning: any = { placement: 'right-end' };

const icons = {
  profile: <ProfileIcon />,
  qrCode: <QrCodeIcon />,
  settings: <SettingIcon />,
  logout: <LogoutIcon />,
  logReport: <LogReportIcon />,
};

export const MyOperations = memo(function MyOperations() {
  const { t } = useTranslation();
  const { logout } = useSessionStore();
  const [editFormVisible, setEditFormVisible] = useState(false);
  const [editItem, setEditItem] = useState<EditItem>(EditItem.Bio);
  const [openLogout, setOpenLogout] = useState(false);

  const onHandler = useCallback((action: string) => {
    switch (action) {
      case 'logout':
        setOpenLogout(true);
        break;
      case EditItem.Name:
        setEditItem(EditItem.Name);
        setEditFormVisible(true);
        break;
      case EditItem.Bio:
        setEditItem(EditItem.Bio);
        setEditFormVisible(true);
        break;
    }
  }, []);

  return (
    <>
      <Menu.Root positioning={menuPositioning} lazyMount unmountOnExit>
        <Menu.Trigger as="div">
          <MyAvatar size="sm"></MyAvatar>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              display="flex"
              flexDirection="column"
              fontSize="sm"
              fontWeight="semibold"
              gap="0"
              w="360px"
              bg="bg.subtle"
              p="0"
            >
              <MyOperationsMain onHandler={onHandler}></MyOperationsMain>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
      <EditFormDialog
        title={t('buttons.edit')}
        item={editItem}
        show={editFormVisible}
        setShow={setEditFormVisible}
      />
      <ConfirmDialog
        open={openLogout}
        onClose={() => setOpenLogout(false)}
        title={t('profile.logoutTitle')}
        onOk={() => logout()}
        okText={t('profile.logout')}
      >
        <Box>{t('profile.logoutContent')}</Box>
      </ConfirmDialog>
    </>
  );
});

export interface MyOperationsMainProps {
  onHandler: (action: string) => void;
}

export function MyOperationsMain({ onHandler }: MyOperationsMainProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setProfileOpen, setProfileUser, setQrCodeIsOpen } = useActionsStore(
    useShallowEnhance(['setProfileOpen', 'setProfileUser', 'setQrCodeIsOpen'])
  );
  const { client, user } = useAppStore();
  const { countryLabel } = useCountries(user?.location || '');
  const refLogin = useRef(onHandler);
  refLogin.current = onHandler;

  const onLogReportOpenChange = useActionsStore((set) => set.onLogReportOpenChange);

  const menuItems = [
    [
      {
        label: t('profile.profileTitle'),
        value: 'profile',
        icon: icons.profile,
        color: 'fg',
        // hoverBg: 'bg.muted',
      },
      {
        label: t('profile.qrcode'),
        value: 'qr-code',
        icon: icons.qrCode,
        color: 'fg',
        // hoverBg: 'bg.muted',
      },
    ],
    [
      {
        label: t('profile.logReport'),
        value: 'logReport',
        icon: icons.logReport,
        color: 'fg',
      },
      {
        label: t('profile.settings'),
        value: 'settings',
        icon: icons.settings,
        color: 'fg',
        // hoverBg: 'bg.muted',
      },
    ],
    [
      {
        label: t('profile.logout'),
        value: 'logout',
        icon: icons.logout,
        color: 'fg.error',
        // hoverBg: 'bg.error',
      },
    ],
  ];

  const onSelect = useCallback(
    async (action: string) => {
      switch (action) {
        case 'settings':
          navigate(Paths.SETTINGS);
          break;
        case 'profile':
          setProfileUser(user);
          setProfileOpen(true);
          break;
        case 'qr-code': {
          client?.Api.common
            .getShareUrl({
              user_id: user?.user_id || '',
            })
            .then(({ url }) => {
              setQrCodeIsOpen(true, {
                link: url || '',
                avatar: user?.avatar || '',
                name: user?.name || '',
                username: user?.username || '',
                source: 'user',
              });
            });
          break;
        }
        case 'logout':
          refLogin.current?.('logout');
          break;

        case 'logReport':
          onLogReportOpenChange(true);
          break;
      }
    },
    [client?.Api.common, navigate, setProfileOpen, setProfileUser, setQrCodeIsOpen, user]
  );

  // const handleUploadAvatar = useCallback(
  //   async (file: File) => {
  //     await client?.Api.user.uploadAvatar({ file });
  //     handleUpdateProfileUser();
  //   },
  //   [client?.Api.user, handleUpdateProfileUser]
  // );

  return (
    <>
      <Box className="Header">
        <Box p="1" pb="0" bg="bg">
          <Box mx="-1" mt="-1" h="160px" overflow="hidden">
            <Image src={MyCenterBg} alt="bg" width="full" />
          </Box>
          <Box pos="relative" h="50px">
            <Box
              fontSize="sm"
              pos="absolute"
              bottom="-70%"
              left="3"
              right="0"
              transform="translateY(-50%)"
            >
              <Flex
                alignItems="flex-end"
                gap="4"
                css={{
                  '--avatar-width': 'calc(var(--text-scale-factor, 0px) + 64px)',
                }}
              >
                <Box h="var(--avatar-width)" onClick={() => onSelect('profile')}>
                  <Menu.Item value="profile" bg="transparent" p="0">
                    <Box
                      pos="relative"
                      cursor="pointer"
                      css={{
                        '&:hover': {
                          '--show-update-avatar': 'flex',
                        },
                      }}
                    >
                      <MyAvatar
                        outlineWidth="2px"
                        outlineColor="bg"
                        outlineStyle="solid"
                        giveWidth="calc(var(--text-scale-factor, 0px) + 64px)"
                        floatFontSize="1.1em"
                      />
                      <Flex
                        pos="absolute"
                        top={0}
                        left={0}
                        alignItems="center"
                        justifyContent="center"
                        zIndex={10}
                        w="var(--avatar-width)"
                        height="var(--avatar-width)"
                        bg="whiteAlpha.10"
                        rounded="50%"
                        display="var(--show-update-avatar, none)"
                      />
                    </Box>
                  </Menu.Item>
                </Box>

                <Box flex="1" overflow="hidden">
                  <Flex
                    fontSize="xl"
                    fontWeight="bold"
                    alignItems="center"
                    gap="1"
                    css={{
                      '&:hover': {
                        '--show-copy-name': 'block',
                      },
                    }}
                  >
                    <Box
                      truncate
                      onClick={() => tools.copyToClipboard(user?.name)}
                      cursor="pointer"
                      maxW="70%"
                    >
                      {user?.name}
                    </Box>
                    <GenderAge gender={user?.gender} age={user?.birthday} />
                    <Center
                      display="var(--show-copy-name, none)"
                      cursor="pointer"
                      color="fg.muted"
                      onClick={() => tools.copyToClipboard(user?.name)}
                    >
                      <CopyIcon />
                    </Center>
                  </Flex>
                  <Flex
                    alignItems="center"
                    fontSize="xs"
                    color="fg.muted"
                    fontWeight="medium"
                    css={{
                      '&:hover': {
                        '--show-copy-name': 'block',
                      },
                    }}
                  >
                    <Box
                      minW="50px"
                      truncate
                      onClick={() => {
                        tools.copyToClipboard(`@${user?.username}`);
                      }}
                      cursor="pointer"
                    >
                      @{user?.username}
                    </Box>
                    {user?.location && (
                      <>
                        <Separator
                          h="1em"
                          orientation="vertical"
                          mx="1.5"
                          borderColor="border.muted"
                        />
                        <Box truncate>{countryLabel}</Box>
                      </>
                    )}
                    <Center
                      display="var(--show-copy-name, none)"
                      cursor="pointer"
                      color="fg.muted"
                      mx="1"
                      onClick={() => {
                        tools.copyToClipboard(`@${user?.username}`);
                      }}
                    >
                      <CopyIcon />
                    </Center>
                  </Flex>
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>
        <Box p="3" bg="linear-gradient(to bottom, var(--colors-bg), var(--colors-bg-subtle))">
          <HStack
            color={user?.bio ? 'fg.muted' : 'fg.subtle'}
            cursor="pointer"
            fontSize="sm"
            fontWeight="medium"
            overflow="hidden"
            gap="1"
            onClick={() => {
              refLogin.current?.(EditItem.Bio);
            }}
            css={{
              '&:hover': {
                '--show-edit-bio': 'block',
              },
            }}
          >
            {user?.bio ? (
              <Span lineClamp={1}>{user?.bio}</Span>
            ) : (
              <Span>{t('profile.bioPlaceholder')}</Span>
            )}

            <Box flex="1" display="var(--show-edit-bio,none)">
              <EditIconGhost />
            </Box>
          </HStack>
        </Box>
      </Box>
      <Flex direction="column" gap="2" px="3" pb="1">
        {menuItems.map((item, index) => (
          <Box key={index} bg="bg" borderRadius="lg" overflow="hidden">
            {item.map((it) => (
              <Menu.Item
                value={it.value}
                key={it.label}
                cursor="pointer"
                color={it.color}
                onClick={() => onSelect(it.value)}
                bg="bg"
                px="2"
                py="0"
              >
                <Flex w="full" gap="2.5" alignItems="center">
                  <Box fontSize="lg">{it.icon}</Box>
                  <Text flex="1" py="3" borderBottom="1px solid" borderColor="border.subtle">
                    {it.label}
                  </Text>
                </Flex>
              </Menu.Item>
            ))}
          </Box>
        ))}
      </Flex>
    </>
  );
}
